import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface DishCardProps {
  number?: string;
  name: string;
  description?: string;
  price: string;
  variants?: string;
  allergens?: string;
  isCompact?: boolean;
}

const DishCard = ({ number, name, description, price, variants, allergens, isCompact = true }: DishCardProps) => {
  return (
    <Card className="group relative hover:shadow-2xl hover:shadow-gold/10 transition-all duration-500 border-border/40 hover:border-gold/50 bg-gradient-to-br from-card via-card to-luxury/50 backdrop-blur-sm overflow-hidden">
      {/* Decorative corner accent */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-gold/5 to-transparent"></div>
      
      <CardContent className={isCompact ? "p-2 md:p-3 relative" : "p-6 md:p-8 relative"}>
        <div className={isCompact ? "flex justify-between items-start gap-3" : "flex justify-between items-start gap-6"}>
          <div className="flex-1">
            <div className={isCompact ? "flex items-baseline gap-1.5 mb-1 flex-wrap" : "flex items-baseline gap-3 mb-4 flex-wrap"}>
              <span className={isCompact ? "text-gold font-bold text-xs md:text-sm" : "text-gold font-bold text-base md:text-lg"}>{number}.</span>
              <h3 className={isCompact ? "text-sm md:text-base font-serif font-bold text-foreground group-hover:text-gold transition-colors duration-300" : "text-xl md:text-2xl font-serif font-bold text-foreground group-hover:text-gold transition-colors duration-300"}>
                {name}
              </h3>
              {variants && (
              <span className={isCompact 
                  ? "text-xs md:text-sm text-gold font-semibold font-serif ml-auto tracking-wide" 
                  : "text-sm md:text-base text-gold font-semibold font-serif ml-auto tracking-wide"
                } style={{ textShadow: '0 0 15px hsl(var(--gold) / 0.3)' }}>
                  {variants}
                </span>
              )}
            </div>
            <p className={isCompact ? "text-muted-foreground leading-tight mb-1 text-[11px] md:text-xs" : "text-muted-foreground leading-relaxed mb-4 text-sm md:text-base"}>
              {description}
            </p>
            {allergens && (
              <div className={isCompact ? "flex flex-wrap gap-1 mt-1" : "flex flex-wrap gap-2 mt-4"}>
                {allergens.split(',').map((allergen, idx) => (
                  <Badge 
                    key={idx} 
                    variant="outline" 
                    className={isCompact ? "text-[9px] px-1 py-0 h-4 border-gold/30 bg-luxury/50 text-gold-light hover:bg-gold/10 transition-colors" : "text-xs border-gold/30 bg-luxury/50 text-gold-light hover:bg-gold/10 transition-colors"}
                  >
                    {allergen.trim()}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          {!variants && (
            <div className="flex flex-col items-end flex-shrink-0">
              <div className="relative">
                <span className={isCompact ? "text-lg md:text-xl font-bold text-gold font-serif drop-shadow-lg" : "text-3xl md:text-4xl font-bold text-gold font-serif drop-shadow-lg"}>
                  {price}
                </span>
                <span className={isCompact ? "text-[9px] text-gold-light uppercase tracking-wider ml-0.5" : "text-xs text-gold-light uppercase tracking-wider ml-1"}>kr</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DishCard;
